import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Person} from "../../../services/interface/query/person";
import {PhoneInterface} from "../../../services/interface/query/phone-interface";
import {AddressInterface} from "../../../services/interface/query/address-interface";
import {
    useFindPersonAddress,
    useFindPersonEmails,
    useFindPersonPhone,
    useFindPersonPhoneHistory
} from "../../../services/apis/apiLocation";
import NotPermissionComponent from "../NotPermissionComponent";
import NoRecords from "../../components/NoRecords";
import {EmailInterface} from "../../../services/interface/query/email-interface";

interface Props {
    person?: Person;
}

export default function Locations({person}: Props) {
    const {
        mutate: getPersonAddress,
        isPending: loadingPersonAddress,
        error: errorPersonAddress,
        data: successPersonAddress,
    } = useFindPersonAddress()

    const {
        mutate: getPersonPhone,
        isPending: loadingPersonPhone,
        error: errorPersonPhone,
        data: successPersonPhone,
    } = useFindPersonPhone()

    const {
        mutate: getPersonPhoneHistory,
        isPending: loadingPersonPhoneHistory,
        error: errorPersonPhoneHistory,
        data: successPersonPhoneHistory,
    } = useFindPersonPhoneHistory()

    const {
        mutate: getPersonEmails,
        isPending: loadingPersonEmails,
        error: errorPersonEmails,
        data: successPersonEmails,
    } = useFindPersonEmails()

    const {t} = useTranslation("global") as { t: (key: string) => string };
    const [location, setLocation] = useState<AddressInterface[] | undefined>()
    const [phones, setPhones] = useState<PhoneInterface[] | undefined>()
    const [phonesHistory, setPhonesHistory] = useState<PhoneInterface[] | undefined>()
    const [emails, setEmails] = useState<EmailInterface[] | undefined>()

    useEffect(() => {
        if (person && person.identification) {
            setLocation(undefined)
            setPhones(undefined)
            setEmails(undefined)
            setPhonesHistory(undefined)
            getPersonAddress({identifier: person.identification, msj: t('addresses')})
            getPersonPhone({identifier: person.identification, msj: t('phones')})
            getPersonPhoneHistory({identifier: person.identification, msj: `${t('historical')} - ${t('phones')}`})
            getPersonEmails({identifier: person.identification, msj: t('emails')})
        }
    }, [person]);

    useEffect(() => {
        if (successPersonAddress) {
            setLocation(successPersonAddress)
        }
    }, [successPersonAddress]);

    useEffect(() => {
        if (successPersonPhone) {
            setPhones(successPersonPhone)
        }
    }, [successPersonPhone]);

    useEffect(() => {
        if (successPersonPhoneHistory) {
            setPhonesHistory(successPersonPhoneHistory)
        }
    }, [successPersonPhoneHistory]);

    useEffect(() => {
        if (successPersonEmails) {
            setEmails(successPersonEmails)
        }
    }, [successPersonEmails]);

    const classLocation = 'pb-2 border border-b-1 border-b-secondary border-transparent';

    return (
        <div className={'flex flex-col gap-4'}>
            <div className={'grid grid-cols-1 lg:grid-cols-2 gap-4'}>
                <div className={'custom-card p-4'}>
                    <h3 className={classLocation}>
                        {t('addresses')}</h3>
                    <NotPermissionComponent
                        title={'/information/address'}
                        loading={loadingPersonAddress}
                        error={errorPersonAddress}
                        className={'mt-2 min-h-[50px]'}
                        component={
                            <>{
                                (location && !loadingPersonAddress && location?.length > 0) ?
                                    location.map((address, index) => (
                                        <div key={`address-${index}`} className={'flex gap-2 mt-3'}>
                                <span className={`text-empColorPrimary text-[14px] w-[25%] uppercase`}
                                >{`#${index + 1} ${address.type}:`}</span>
                                            <span className={'text-[13px] w-[75%] font-light uppercase'}>{`
                                ${address.country ? address.country + ', ' : ''}
                                ${address.prov ? address.prov + ', ' : ''}
                                ${address.cant ? address.cant + ', ' : ''}
                                ${address.parr ? address.parr + ', ' : ''}
                                ${address.streetName ? address.streetName : address.reference}`}
                                            </span>
                                        </div>
                                    )) : <NoRecords className={'mt-2'}/>
                            }</>
                        }/>
                </div>
                <div className={'custom-card p-4'}>
                    <h3 className={classLocation}>{t('emails')}</h3>
                    <NotPermissionComponent
                        title={'/information/email'}
                        loading={loadingPersonEmails}
                        error={errorPersonEmails}
                        className={'mt-2 min-h-[50px]'}
                        component={
                            <>{
                                (emails && !loadingPersonEmails && emails?.length > 0) ?
                                    emails.map((mail, index) => (
                                        <div key={`emails-${index}`} className={'flex flex-col md:flex-row lg:flex-col xl:flex-row gap-2 mt-3'}>
                                            <span className={'text-empColorPrimary text-[14px] uppercase'}>{`#${index + 1} ${t('register')}:`}</span>
                                            <span className={'text-[14px] font-light'}>{mail?.mail}</span>
                                            {mail.mailType &&
                                                <div className={'flex flex-row ml-auto gap-2'}>
                                                    <span
                                                        className={'text-empColorPrimary text-[14px] uppercase'}>{`${t('type')}:`}</span>
                                                    <span className={'text-[14px] font-light'}>{mail?.mailType}</span>
                                                </div>
                                            }
                                        </div>
                                    )) : <NoRecords className={'mt-2'}/>
                            }</>
                        }
                    />
                </div>
                <div className={'custom-card p-4'}>
                    <h3 className={classLocation}>{t('phones')}</h3>
                    <NotPermissionComponent
                        title={'/information/telephone'}
                        loading={loadingPersonPhone}
                        error={errorPersonPhone}
                        className={'mt-2 min-h-[50px]'}
                        component={
                            <>{
                                (phones && !loadingPersonPhone && phones?.length > 0) ?
                                    phones.map((phone, index) => (
                                        <div key={`phone-${index}`} className={'flex gap-2 mt-3'}>
                                <span
                                    className={'text-empColorPrimary text-[14px] uppercase'}>{`#${index + 1} ${t('register')}:`}</span>
                                            <span className={'text-[14px] font-light'}>{phone.numberPhone}</span>
                                        </div>
                                    )) : <NoRecords className={'mt-2'}/>
                            }</>
                        }
                    />
                </div>
                <div className={'custom-card p-4'}>
                    <h3 className={classLocation}>{`${t('historical')} - ${t('phones')}`}</h3>
                    <NotPermissionComponent
                        title={'/information/history/telephone'}
                        loading={loadingPersonPhoneHistory}
                        error={errorPersonPhoneHistory}
                        className={'mt-2 min-h-[50px]'}
                        component={
                            <>{
                                (phonesHistory && !loadingPersonPhoneHistory && phonesHistory?.length > 0) ?
                                    phonesHistory.map((historical, index) => (
                                        <div key={`historical-${index}`} className={'flex gap-2 mt-3'}>
                            <span
                                className={'text-empColorPrimary text-[14px] uppercase'}>{`#${index + 1} ${t('register')}:`}</span>
                                            <span className={'text-[14px] font-light'}>{historical.numberPhone}</span>
                                        </div>
                                    )) : <NoRecords className={'mt-2'}/>
                            }</>
                        }
                    />
                </div>
            </div>
        </div>
    )
}
